import React, { useEffect, useState, useContext } from 'react';
import api from '../utils/api';
import { track } from '../utils/analytics';
import { UserContext } from '../App';

export default function WillForm() {
  const { currentUser } = useContext(UserContext);
  const [metadata, setMetadata] = useState(null);
  const [jurisdiction, setJurisdiction] = useState('ENGLAND_WALES');
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);

  // Resolve API base for absolute download/preview links
  const apiBase = (typeof window !== 'undefined' && window.__API_BASE_URL__) || process.env.REACT_APP_API_BASE_URL || ((typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) ? 'https://127.0.0.1:3001' : '');

  // Form state (v1 minimal, expandable)
  const [testator, setTestator] = useState({ fullName: '', address: '' });
  const [executors, setExecutors] = useState([{ fullName: '' }]);
  const [substitutes, setSubstitutes] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [residueShares, setResidueShares] = useState([{ beneficiaryName: '', percentage: 100 }]);
  const [survivorshipDays, setSurvivorshipDays] = useState(28);
  const [guardians, setGuardians] = useState([]);
  const [professionalExecutorCharging, setProfessionalExecutorCharging] = useState(false);

  useEffect(() => {
    track('will_form_viewed', { userId: currentUser?.id });
    api.get('/will/template-metadata').then(res => setMetadata(res.data));
  }, [currentUser]);

  const validate = () => {
    const e = {};
    if (!testator.fullName) e.testator = 'Testator full name is required';
    if (!jurisdiction) e.jurisdiction = 'Jurisdiction is required';
    if (executors.length < 1 || executors.length > 4 || executors.some(x => !x.fullName)) e.executors = '1–4 executors with names required';
    const total = residueShares.reduce((a,b)=> a + Number(b.percentage||0), 0);
    if (Math.round(total) !== 100) e.residue = 'Residue must total 100%';
    if (!ack) e.ack = 'Please confirm witness rule acknowledgement';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const generate = async () => {
    if (!validate()) return;
    setLoading(true);
    setResult(null);
    try {
      track('will_form_submitted', { userId: currentUser?.id, jurisdiction, executorCount: executors.length, giftCount: gifts.length, residueCount: residueShares.length });
      const payload = {
        jurisdiction,
        testator,
        executors: executors.map(e => ({ fullName: e.fullName, address: e.address || '', email: e.email || '', isProfessional: !!e.isProfessional })),
        substitutes: substitutes.map(e => ({ fullName: e.fullName, address: e.address || '', email: e.email || '', isProfessional: !!e.isProfessional })),
        guardians: guardians.map(g => ({ fullName: g.fullName, address: g.address || '', ageOfVesting: String(g.ageOfVesting || 18) })),
        gifts: gifts.map(g => ({
          type: g.type,
          description: g.description,
          amount: g.type === 'CASH' ? Number(g.amount) : undefined,
          currency: g.type === 'CASH' ? 'GBP' : undefined,
          beneficiaryName: g.beneficiaryName,
          relationship: g.relationship || '',
          isCharity: !!g.isCharity,
          charityNumber: g.charityNumber || '',
          fallback: { mode: g.fallbackMode || 'RESIDUE', redirectToName: g.redirectToName || undefined }
        })),
        residueShares: residueShares.map(r => ({ beneficiaryName: r.beneficiaryName, percentage: Number(r.percentage) })),
        survivorshipDays: Number(survivorshipDays) || 28,
        revocation: true,
        professionalExecutorCharging,
        meta: { createdAt: new Date().toISOString(), createdBy: currentUser?.id || '' }
      };
      track('will_generation_started', { userId: currentUser?.id, jurisdiction });
      const res = await api.post('/will/generate', payload);
      setResult(res.data);
      track('will_generation_succeeded', { userId: currentUser?.id });
    } catch (err) {
      console.error(err);
      track('will_generation_failed', { userId: currentUser?.id, errorCode: String(err?.response?.status || '500') });
      alert('Failed to generate will. Please review inputs and try again.');
    } finally {
      setLoading(false);
    }
  };

  const addExecutor = () => setExecutors(xs => xs.length < 4 ? [...xs, { fullName: '' }] : xs);
  const removeExecutor = (idx) => setExecutors(xs => xs.filter((_,i)=>i!==idx));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Jurisdiction & Basics */}
      <div style={{ background: '#fff', border: '1px solid var(--mist-300)', borderRadius: 12, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Jurisdiction & Basics</h3>
        <label>Jurisdiction<br/>
          <select value={jurisdiction} onChange={e=>setJurisdiction(e.target.value)} aria-label="Jurisdiction" required>
            <option value="ENGLAND_WALES">England & Wales</option>
            <option value="SCOTLAND">Scotland</option>
            <option value="NORTHERN_IRELAND">Northern Ireland</option>
          </select>
        </label>
        <div style={{ color: '#666', marginTop: 6, fontSize: 14 }}>{metadata?.witnessGuidance?.[jurisdiction]}</div>
        <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
          <label>Testator full legal name<br/>
            <input value={testator.fullName} onChange={e=>setTestator(t=>({ ...t, fullName: e.target.value }))} aria-label="Testator full name" required/>
          </label>
          <label>Current address (optional)<br/>
            <textarea value={testator.address} onChange={e=>setTestator(t=>({ ...t, address: e.target.value }))} aria-label="Testator address"/>
          </label>
        </div>
      </div>

      {/* Executors */}
      <div style={{ background: '#fff', border: '1px solid var(--mist-300)', borderRadius: 12, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Executors</h3>
        {executors.map((ex, idx) => (
          <div key={idx} style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr', alignItems: 'end', marginBottom: 8 }}>
            <label style={{ gridColumn: '1 / span 2' }}>Name<br/>
              <input value={ex.fullName} onChange={e=>setExecutors(xs=> xs.map((x,i)=> i===idx ? { ...x, fullName: e.target.value } : x))} required aria-label={`Executor ${idx+1} name`}/>
            </label>
            <label>Address (optional)<br/>
              <input value={ex.address||''} onChange={e=>setExecutors(xs=> xs.map((x,i)=> i===idx ? { ...x, address: e.target.value } : x))} aria-label={`Executor ${idx+1} address`}/>
            </label>
            <label>Email (optional)<br/>
              <input value={ex.email||''} onChange={e=>setExecutors(xs=> xs.map((x,i)=> i===idx ? { ...x, email: e.target.value } : x))} aria-label={`Executor ${idx+1} email`}/>
            </label>
            {executors.length > 1 && (
              <button type="button" onClick={()=>removeExecutor(idx)} style={{ justifySelf: 'start', marginTop: 4 }}>Remove</button>
            )}
          </div>
        ))}
        <button type="button" onClick={addExecutor} disabled={executors.length>=4}>Add executor</button>
      </div>

      {/* Residue */}
      <div style={{ background: '#fff', border: '1px solid var(--mist-300)', borderRadius: 12, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Residue</h3>
        <div style={{ color: '#555', fontSize: 14, marginBottom: 8 }}>Per capita distribution. Percentages must total 100%.</div>
        {residueShares.map((r, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 120px auto', gap: 8, alignItems: 'end', marginBottom: 8 }}>
            <label>Name<br/>
              <input value={r.beneficiaryName} onChange={e=>setResidueShares(rs=>rs.map((x,i)=> i===idx ? { ...x, beneficiaryName: e.target.value } : x))} aria-label={`Residue beneficiary ${idx+1}`}/>
            </label>
            <label>Percentage<br/>
              <input type="number" min="0" max="100" value={r.percentage} onChange={e=>setResidueShares(rs=>rs.map((x,i)=> i===idx ? { ...x, percentage: e.target.value } : x))} aria-label={`Residue percentage ${idx+1}`}/>
            </label>
            <button type="button" onClick={()=>setResidueShares(rs=>rs.filter((_,i)=>i!==idx))} disabled={residueShares.length<=1}>Remove</button>
          </div>
        ))}
        <button type="button" onClick={()=>setResidueShares(rs=>[...rs, { beneficiaryName: '', percentage: 0 }])}>Add beneficiary</button>
        <div style={{ marginTop: 10 }}>
          <label>Survivorship days (7–90, default 28)<br/>
            <input type="number" min="7" max="90" value={survivorshipDays} onChange={e=>setSurvivorshipDays(e.target.value)} aria-label="Survivorship days"/>
          </label>
        </div>
      </div>

      {/* Final Checks */}
      <div style={{ background: '#fff', border: '1px solid var(--mist-300)', borderRadius: 12, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Final Checks & Guidance</h3>
        <div style={{ fontSize: 14, color: '#444' }}>
          <p><b>Witness Guidance</b>: {metadata?.witnessGuidance?.[jurisdiction]}</p>
          <p><b>Registration Guidance</b>: Consider registering your will; store the original safely.</p>
          <p style={{ color: '#7a5' }}><b>Disclaimer</b>: This generator produces a simple will suited to straightforward estates. It is not legal advice.</p>
        </div>
        <label>
          <input type="checkbox" checked={ack} onChange={e=>setAck(e.target.checked)} aria-label="Witness confirmation"/>
          {' '}I understand beneficiaries or their spouses/civil partners must not witness my signature.
        </label>
        {Object.values(errors).length>0 && (
          <div style={{ color: '#b00020', marginTop: 8 }} role="alert" aria-live="assertive">
            {Object.values(errors).map((m,i)=>(<div key={i}>{m}</div>))}
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={generate} disabled={loading} aria-label="Generate Will" style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--sage-600)', color: '#fff', fontWeight: 700 }}>{loading ? 'Generating…' : 'Generate Will'}</button>
        </div>
        {result && (
          <div style={{ marginTop: 14, display: 'flex', gap: 12 }}>
            {(() => {
              const userIdQs = `?userId=${encodeURIComponent(currentUser?.id || '')}`;
              const docxHref = `${apiBase}${result.docxUrl}${userIdQs}`;
              const pdfHref = `${apiBase}${result.pdfUrl}${userIdQs}`;
              return (
                <>
                  <a href={docxHref} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--mist-200)', textDecoration: 'none', color: 'var(--dusk-800)' }}>Download .docx</a>
                  <a href={pdfHref} target="_blank" rel="noreferrer" style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--mist-200)', textDecoration: 'none', color: 'var(--dusk-800)' }}>Preview PDF</a>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}


